import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getActivityLogs } from '@/services/api';
import { 
  History, 
  UserPlus, 
  UserMinus, 
  UserCheck, 
  UserX,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  FileText,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const AdminActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const actionType = filter === 'all' ? null : filter;
      const data = await getActivityLogs(100, actionType);
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const getActionIcon = (actionType) => {
    const icons = {
      user_registered: <UserPlus className="h-4 w-4 text-blue-600" />,
      user_created: <UserPlus className="h-4 w-4 text-green-600" />,
      user_created_by_admin: <UserPlus className="h-4 w-4 text-green-600" />,
      user_approved: <UserCheck className="h-4 w-4 text-green-600" />,
      user_rejected: <UserX className="h-4 w-4 text-red-600" />,
      tenant_approved_by_resident: <UserCheck className="h-4 w-4 text-blue-600" />,
      user_deleted: <UserMinus className="h-4 w-4 text-red-600" />,
      removal_requested: <UserMinus className="h-4 w-4 text-amber-600" />,
      removal_approved: <CheckCircle2 className="h-4 w-4 text-red-600" />,
      removal_rejected: <XCircle className="h-4 w-4 text-blue-600" />,
      vacancy_requested: <Clock className="h-4 w-4 text-orange-600" />,
      vacancy_approved: <CheckCircle2 className="h-4 w-4 text-orange-600" />,
      vacancy_rejected: <XCircle className="h-4 w-4 text-blue-600" />,
      amenity_booked: <Clock className="h-4 w-4 text-purple-600" />,
      bill_paid: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      rent_submitted: <Clock className="h-4 w-4 text-amber-600" />,
      rent_confirmed: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    };
    return icons[actionType] || <FileText className="h-4 w-4 text-slate-600" />;
  };

  const getActionLabel = (actionType) => {
    const labels = {
      user_registered: 'New Registration',
      user_created: 'User Created',
      user_created_by_admin: 'User Created (Admin)',
      user_approved: 'User Approved',
      user_rejected: 'User Rejected',
      tenant_approved_by_resident: 'Tenant Approved (Resident)',
      user_deleted: 'User Deleted',
      removal_requested: 'Removal Requested',
      removal_approved: 'Removal Approved',
      removal_rejected: 'Removal Rejected',
      vacancy_requested: 'Vacancy Requested',
      vacancy_approved: 'Vacancy Approved',
      vacancy_rejected: 'Vacancy Rejected',
      tenant_added: 'Tenant Added',
      amenity_booked: 'Amenity Booked',
      bill_paid: 'Bill Paid',
      rent_submitted: 'Rent Submitted',
      rent_confirmed: 'Rent Confirmed',
    };
    return labels[actionType] || actionType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Action';
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-activity-log">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Activity Log</h1>
          <p className="text-sm md:text-base text-slate-600">Track all actions and decisions for reference</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-select">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="user_registered">New Registrations</SelectItem>
              <SelectItem value="user_approved">User Approvals</SelectItem>
              <SelectItem value="user_rejected">User Rejections</SelectItem>
              <SelectItem value="user_deleted">User Deletions</SelectItem>
              <SelectItem value="removal_requested">Removal Requests</SelectItem>
              <SelectItem value="removal_approved">Removals Approved</SelectItem>
              <SelectItem value="removal_rejected">Removals Rejected</SelectItem>
              <SelectItem value="vacancy_requested">Vacancy Requests</SelectItem>
              <SelectItem value="vacancy_approved">Vacancies Approved</SelectItem>
              <SelectItem value="vacancy_rejected">Vacancies Rejected</SelectItem>
              <SelectItem value="amenity_booked">Amenity Bookings</SelectItem>
              <SelectItem value="bill_paid">Bill Payments</SelectItem>
              <SelectItem value="rent_submitted">Rent Submissions</SelectItem>
              <SelectItem value="rent_confirmed">Rent Confirmations</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchLogs} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Pending Actions Alert */}
      {logs.some(l => l.status === 'pending') && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">Pending Actions</p>
                <p className="text-sm text-amber-700">
                  {logs.filter(l => l.status === 'pending').length} action(s) require your attention
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-slate-600" />
            Recent Activities
          </CardTitle>
          <CardDescription>All recorded actions with reasons and outcomes</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div 
                  key={log.id} 
                  className={`p-4 rounded-xl border ${log.status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                  data-testid="activity-log-item"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${log.status === 'pending' ? 'bg-amber-100' : 'bg-white'}`}>
                        {getActionIcon(log.action_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-slate-900">{getActionLabel(log.action_type)}</span>
                          {getStatusBadge(log.status)}
                        </div>
                        
                        {/* Action Details */}
                        <div className="text-sm text-slate-600 space-y-1">
                          <p className="flex items-center gap-2">
                            <User className="h-3 w-3" />
                            <span className="font-medium">By:</span> {log.action_by_name} ({log.action_by_role})
                          </p>
                          {log.target_user_name && (
                            <p className="flex items-center gap-2">
                              <User className="h-3 w-3" />
                              <span className="font-medium">Target:</span> {log.target_user_name} ({log.target_user_role})
                            </p>
                          )}
                        </div>

                        {/* Reason */}
                        {log.reason && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-slate-200">
                            <p className="text-xs font-medium text-slate-500 mb-1">Reason / Remarks:</p>
                            <p className="text-sm text-slate-700">{log.reason}</p>
                          </div>
                        )}

                        {/* Details */}
                        {log.details && (
                          <p className="mt-2 text-sm text-slate-600 italic">
                            {typeof log.details === 'string' 
                              ? log.details 
                              : Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-500 whitespace-nowrap">
                      {formatDate(log.created_at || log.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityLog;
